import React, { useState, useRef, useEffect } from 'react';
import { connect, useSelector, useDispatch } from "react-redux";
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, Box,
  TablePagination, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { RiCheckboxCircleLine } from "react-icons/ri";
import { RiFileExcel2Fill } from "react-icons/ri";
import { IoMdEye } from "react-icons/io";
import moment from 'moment';
import { Link } from 'react-router-dom';
import { CSVLink } from "react-csv";
import { MdOutlineSettings, MdConstruction } from "react-icons/md";
import CustomLoadingOverlay from "./CustomLoadingOverlay";
import Tooltip from '@material-ui/core/Tooltip';
import RedemptionModal from './RedemptionModal';
import Alert from '@material-ui/lab/Alert';
import HttpService from '../../services/httpService';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);
const useStyles = makeStyles(() => ({

  cell: {},
  rotateIcon: {
    animation: "$spin 2s linear infinite",
  },

  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },

  root: {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    width: '100%',
    overflowX: 'auto'
  },
  tableContainer: {
    overflowX: 'auto',
    '&::-webkit-scrollbar': {
      height: '6px'
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f1f1',
      borderRadius: '4px'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#1c375d',
      borderRadius: '4px'
    }
  },
  headerCell: {
    backgroundColor: '#1c375d',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '10px',
    padding: '8px 3px',
    textAlign: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    whiteSpace: 'pre-line',
    lineHeight: 1.2,
    wordBreak: 'break-word'
  },
  cell: {
    fontSize: '11px',
    fontWeight: 'bold',
    padding: '8px 3px',
    textAlign: 'center',
    borderRight: '1px solid #e0e0e0',
    whiteSpace: 'nowrap'
  },
  notesCell: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#555',
    padding: '16px',
    textAlign: 'center',
  },
  paginationRoot: {
    width: '100%',
    '& .MuiTablePagination-toolbar': {
      justifyContent: 'flex-start',
      paddingLeft: '16px'
    },
    '& .MuiTablePagination-spacer': {
      display: 'none'
    }
  },
  customTooltip: {
    backgroundColor: '#1c375d',
    border: '2px solid #fdb913',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    fontSize: '13px',
    fontWeight: '500',
    padding: '12px 16px',
    borderRadius: '6px',
    '& ul': {
      fontSize: '12px',
      lineHeight: '1.6',
    },
    '& li': {
      marginBottom: '6px',
    },
    '& strong': {
      fontWeight: '600',
      fontSize: '14px',
    },
  },
}));


const ERPSDashBoard = (props) => {
  const classes = useStyles();
  const { userDetails } = useSelector((state) => state.user);
  const userRoleNames = userDetails?.roles?.map(r => r.name) || [];
  const isAdmin = userRoleNames.includes('Admin') || userRoleNames.includes('Data Analyst');

  // Reward config state — must be declared before cfg
  const [rewardConfig, setRewardConfig] = useState(null);

  // Shorthand config values with fallbacks
  const cfg = {
    revenueMultiplier: rewardConfig?.revenuePoints?.multiplier || 100,
    minAchievementPct: rewardConfig?.redemption?.minAchievementPct || 80,
    minConsecutiveMonths: rewardConfig?.redemption?.minConsecutiveMonths || 3,
    tenurePointsPerYear: rewardConfig?.tenurePoints?.pointsPerYear || 25000,
    npsRequiredScore: rewardConfig?.npsPoints?.requiredScore || 5,
    npsPointsPerProject: rewardConfig?.npsPoints?.pointsPerProject || 1000,
    postNpsRequiredScore: rewardConfig?.postDesignNPS?.requiredScore || 4.5,
    postNpsPoints: rewardConfig?.postDesignNPS?.points || 25000,
    ekomPointsPerProject: rewardConfig?.ekomPoints?.pointsPerProject || 1000,
    ekomMaxDays: rewardConfig?.ekomPoints?.maxDays || 30,
    pointsPerRupee: rewardConfig?.redemption?.pointsPerRupee || 4,
    redemptionTiers: rewardConfig?.redemptionTiers || [],
  };

  const redeemTierTooltip = cfg.redemptionTiers.length > 0
    ? cfg.redemptionTiers
        .filter(t => t.redeemPercentage > 0)
        .map(t => { const p = t.minPoints > 100 ? Math.floor(t.minPoints / 1000) : t.minPoints; return `≥${p}% achievement → ${Math.round(t.redeemPercentage * 100)}% redeemable`; })
        .join(' | ')
    : `≥91% → 100% | ≥81% → 90% | ≥71% → 75% | ≥65% → 50% of Total Points`;

  const COLUMN_TOOLTIPS = {
    'Projects Closed': 'Count of projects with Internal Design Sign-Off',
    'Target': 'Monthly target assigned(₹)',
    'Revenue Achieved': 'Total revenue from closed projects',
    'Achieved (%)': 'Revenue Achieved ÷ Target × 100',
    'Achievement Points': `1 Point per Rs ${cfg.revenueMultiplier} Revenue`,
    'Achievement Points in (Thousands)': `1 Point per Rs ${cfg.revenueMultiplier} Revenue`,
    'Tenure': 'Joining Date: ' + (userDetails?.joiningDate ? new Date(userDetails.joiningDate).toLocaleDateString('en-IN') : 'N/A'),
    'Tenure Points': `1 complete year = ${cfg.tenurePointsPerYear.toLocaleString('en-IN')} pts`,
    'Tenure Points in (Thousands)': <span>1 complete year = {cfg.tenurePointsPerYear.toLocaleString('en-IN')} pts<br />📝 Everyone with 1+ year gets 25k points at the start. You'll earn 25k again on every work anniversary.</span>,
    'Achievement + Tenure Points': 'Achievement Points + Tenure Points',
    'Achievement + Tenure Points (Thousands)': 'Achievement Points + Tenure Points',
    'Design NPS': `NPS = ${cfg.npsRequiredScore}/Project = ${cfg.npsPointsPerProject.toLocaleString('en-IN')} pts`,
    'NPS Points': `NPS = ${cfg.npsRequiredScore}/Project = ${cfg.npsPointsPerProject.toLocaleString('en-IN')} pts`,
    'NPS Points in (Thousands)': `NPS = ${cfg.npsRequiredScore}/Project = ${cfg.npsPointsPerProject.toLocaleString('en-IN')} pts`,
    'Achievement + Tenure + NPS Points': 'Achievement Points + Tenure Points + NPS Points',
    'Achievement + Tenure + NPS Points (Thousands)': 'Achievement Points + Tenure Points + NPS Points',
    'Post Design NPS': 'Avg experience score post design',
    'Post Design NPS Points': <span>Post NPS ≥ {cfg.postNpsRequiredScore} → {cfg.postNpsPoints.toLocaleString('en-IN')} pts<br />📝 Post design NPS will be calculated annually based on monthly average</span>,
    'Post Design NPS Points (Thousands)': <span>Post NPS ≥ {cfg.postNpsRequiredScore} → {cfg.postNpsPoints.toLocaleString('en-IN')} pts<br />📝 Post design NPS will be calculated annually based on monthly average</span>,
    'EKOM to DSO < 30 Days': `Projects where EKOM to Design Sign-off < ${cfg.ekomMaxDays} days`,
    'EKOM Points': `EKOM projects count × ${cfg.ekomPointsPerProject.toLocaleString('en-IN')} pts`,
    'EKOM Points in (Thousands)': `EKOM projects count × ${cfg.ekomPointsPerProject.toLocaleString('en-IN')} pts`,
    'Total Points': 'Achievement + Tenure + Design NPS + Post NPS + EKOM Points',
    'Total Points in (Thousands)': 'Achievement + Tenure + Design NPS + Post NPS + EKOM Points',
    'Redeem Points': redeemTierTooltip,
    'Redeem Points in (Thousands)': redeemTierTooltip,
  };
  const {
    rewardsData = [],
    loading,
    viewType,
    showToggle,
    viewMode,
    onViewModeChange,
    myRewardsDateFilter,
    setMyRewardsDateFilter,
    handleMyRewardsDateSubmit
  } = props;
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(false);
  const [selectedRemarks, setSelectedRemarks] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [csvData, setCsvData] = useState([]);
  const csvRef = useRef();
  const [fadeIn, setFadeIn] = useState(false);
  const [rotate, setRotate] = useState(0);
  const [scale, setScale] = useState(1);
  const [fade, setFade] = useState(false);
  const [leadsModalOpen, setLeadsModalOpen] = useState(false);
  const [leadsData, setLeadsData] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [selectedMonthData, setSelectedMonthData] = useState(null);
  const [redemptionModalOpen, setRedemptionModalOpen] = useState(false);
  const [selectedUserForRedemption, setSelectedUserForRedemption] = useState(null);
  const [projectsModalOpen, setProjectsModalOpen] = useState(false);
  const [selectedODMData, setSelectedODMData] = useState(null);
  const [projectsModalNPSLoading, setProjectsModalNPSLoading] = useState(false);
  // DGM/Admin drill-down: AGM -> ODMs modal
  const [agmODMsModalOpen, setAgmODMsModalOpen] = useState(false);
  const [selectedAGMData, setSelectedAGMData] = useState(null);
  // DGM drill-down level 2: ODM -> Designers modal
  const [odmDesignersModalOpen, setODMDesignersModalOpen] = useState(false);
  const [selectedODMForDGM, setSelectedODMForDGM] = useState(null);
  const [odmDesignersNPSLoading, setOdmDesignersNPSLoading] = useState(false);

  // EKOM modal state
  const [ekomModalOpen, setEkomModalOpen] = useState(false);
  const [selectedEkomData, setSelectedEkomData] = useState(null);

  useEffect(() => {
    HttpService.getRewardConfig().then(res => {
      if (res?.data?.success) setRewardConfig(res.data.data);
    }).catch(() => {});
  }, []);

  // Post Design NPS modal state
  const [postNpsModalOpen, setPostNpsModalOpen] = useState(false);
  const [selectedPostNpsData, setSelectedPostNpsData] = useState(null);

  // My Rewards Actions column modals
  const [eligibilityCriteriaModalOpen, setEligibilityCriteriaModalOpen] = useState(false);
  const [redemptionInfoModalOpen, setRedemptionInfoModalOpen] = useState(false);
  const [selectedEligibilityRow, setSelectedEligibilityRow] = useState(null);

  // Counter animation for Total Earned Rewards
  const [displayedPoints, setDisplayedPoints] = useState(0);
  const [targetTotalPoints, setTargetTotalPoints] = useState(0);

  // Chart modal state
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [monthlyChartData, setMonthlyChartData] = useState(null);


  useEffect(() => {
    const interval = setInterval(() => {
      setRotate(prev => prev + 50);
      setScale(prev => (prev === 1 ? 1.1 : 1));
    }, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setTimeout(() => setFade(true), 200);
  }, []);

  useEffect(() => {
    setTimeout(() => setFadeIn(true), 100);
  }, []);

  // Counter animation for Total Earned Rewards
  useEffect(() => {
    // Find total row from rewardsData
    const totalRow = rewardsData.find(row => row.isTotal === true);
    const totalPoints = totalRow?.totalPoints || 0;

    setTargetTotalPoints(totalPoints);
    setDisplayedPoints(0); // Reset to 0 before animation

    if (totalPoints === 0) return;

    // Animate counter from 0 to totalPoints
    const duration = 2000; // 2 seconds
    const steps = 60; // 60 increments for smooth animation
    const increment = totalPoints / steps;
    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      if (currentStep <= steps) {
        setDisplayedPoints(Math.ceil(increment * currentStep));
      } else {
        setDisplayedPoints(totalPoints); // Ensure final value is exact
        clearInterval(interval);
      }
    }, duration / steps);

    return () => clearInterval(interval);
  }, [rewardsData]);

  // Prepare monthly chart data
  const getMonthlyChartData = () => {
    if (!rewardsData || rewardsData.length === 0) return null;

    // Filter out the total row and extract month-wise data
    const monthlyData = rewardsData
      .filter(row => !row.isTotal)
      .map(row => ({
        month: row.monthLabel || row.month,
        points: row.totalPoints || 0
      }))
      .sort((a, b) => {
        // Sort by month number if available
        const aMonth = parseInt(a.month);
        const bMonth = parseInt(b.month);
        if (!isNaN(aMonth) && !isNaN(bMonth)) {
          return aMonth - bMonth;
        }
        return 0;
      });

    if (monthlyData.length === 0) return null;

    return {
      labels: monthlyData.map(d => d.month),
      datasets: [
        {
          label: 'Total Points',
          data: monthlyData.map(d => d.points),
          borderColor: '#fdb913',
          backgroundColor: 'rgba(253, 185, 19, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 6,
          pointBackgroundColor: '#fdb913',
          pointBorderColor: '#1c375d',
          pointBorderWidth: 2,
          pointHoverRadius: 8
        }
      ]
    };
  };

  const paginatedData = rewardsData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRemarks([]);
  };

  const handleOpenLeadsModal = async (row) => {
    setSelectedMonthData(row);
    setLeadsModalOpen(true);
    setLeadsLoading(true);

    try {
      // Fetch leads with NPS scores
      const leadsWithNPS = await Promise.all((row.closedLeadsData || []).map(async (lead) => {
        // Fetch NPS score for this lead
        try {
          const response = await HttpService.getLeadNPS(lead._id);
          return {
            ...lead,
            npsScore: response?.data?.npsScore || 'Not Available'
          };
        } catch (error) {
          return {
            ...lead,
            npsScore: 'Not Available'
          };
        }
      }));

      // Sort by NPS score in descending order
      const sortedLeads = leadsWithNPS.sort((a, b) => {
        const npsA = a.npsScore === 'Not Available' ? -1 : (typeof a.npsScore === 'number' ? a.npsScore : 0);
        const npsB = b.npsScore === 'Not Available' ? -1 : (typeof b.npsScore === 'number' ? b.npsScore : 0);
        return npsB - npsA;
      });
      setLeadsData(sortedLeads);
    } catch (error) {
      console.error('Error fetching NPS data:', error);
      setLeadsData(row.closedLeadsData || []);
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleCloseLeadsModal = () => {
    setLeadsModalOpen(false);
    setLeadsData([]);
    setSelectedMonthData(null);
  };

  // ---- Handler for ODM projects/designers modal (AGM view) ----
  const handleOpenProjectsModal = async (row, isNpsClick = false) => {
    // Support ODM rows (projectsWithDesigners) and regular designer rows (closedLeadsData)
    let rowData = null;
    if (row?.isODMRow && row?.projectsWithDesigners) {
      rowData = { ...row, isNpsClick };
    } else if (row?.closedLeadsData) {
      // Convert designer leads to projectsWithDesigners format for the modal
      rowData = {
        ...row,
        isODMRow: true,
        isNpsClick,
        projectsWithDesigners: (row.closedLeadsData || []).map(l => ({
          leadId: l._id,
          leadNo: l.lead_no,
          customerName: l.customerName || null,
          designerName: row.userName || 'N/A',
          revenue: l.staticDesignWonBudget || 0,
          internalDesignSignOffDate: l.internalDesignSignOffDate,
          npsScore: null // will be fetched
        }))
      };
    }
    if (!rowData) return;

    setSelectedODMData(rowData);
    setProjectsModalOpen(true);
    setProjectsModalNPSLoading(true);

    // Fetch NPS scores for each project
    try {
      const projectsWithNPS = await Promise.all(
        (rowData.projectsWithDesigners || []).map(async (project) => {
          try {
            const response = await HttpService.getLeadNPS(project.leadId);
            return { ...project, npsScore: response?.data?.npsScore ?? 'N/A' };
          } catch {
            return { ...project, npsScore: 'N/A' };
          }
        })
      );
      // Sort by NPS score in descending order
      const sortedProjects = projectsWithNPS.sort((a, b) => {
        const npsA = a.npsScore === 'N/A' ? -1 : (typeof a.npsScore === 'number' ? a.npsScore : parseFloat(a.npsScore) || 0);
        const npsB = b.npsScore === 'N/A' ? -1 : (typeof b.npsScore === 'number' ? b.npsScore : parseFloat(b.npsScore) || 0);
        return npsB - npsA;
      });

      setSelectedODMData(prev => prev ? { ...prev, projectsWithDesigners: sortedProjects } : prev);
    } catch (err) {
      console.error('Error fetching NPS for projects:', err);
    } finally {
      setProjectsModalNPSLoading(false);
    }
  };

  const handleCloseProjectsModal = () => {
    setProjectsModalOpen(false);
    setSelectedODMData(null);
  };

  // ---- Handler for DGM/Admin AGM -> ODMs modal ----
  const handleOpenAGMODMsModal = (row) => {
    if (row?.isAGMRow && row?.odmsUnderAGM) {
      setSelectedAGMData(row);
      setAgmODMsModalOpen(true);
    }
  };

  const handleCloseAGMODMsModal = () => {
    setAgmODMsModalOpen(false);
    setSelectedAGMData(null);
  };

  // ---- Handler for DGM drill-down level 2: ODM -> Designers modal ----
  const handleOpenODMDesignersModal = async (odmData) => {
    setSelectedODMForDGM(odmData);
    setODMDesignersModalOpen(true);
    setOdmDesignersNPSLoading(true);

    try {
      const projectsWithNPS = await Promise.all(
        (odmData.projectsWithDesigners || []).map(async (project) => {
          try {
            const response = await HttpService.getLeadNPS(project.leadId);
            return { ...project, npsScore: response?.data?.npsScore ?? 'N/A' };
          } catch {
            return { ...project, npsScore: 'N/A' };
          }
        })
      );
      // Sort by NPS score descending (5/5 first, N/A last)
      const sortedProjects = [...projectsWithNPS].sort((a, b) => {
        const npsA = (a.npsScore === 'N/A' || a.npsScore == null) ? -1 : Number(a.npsScore) || 0;
        const npsB = (b.npsScore === 'N/A' || b.npsScore == null) ? -1 : Number(b.npsScore) || 0;
        return npsB - npsA;
      });
      setSelectedODMForDGM(prev => prev ? { ...prev, projectsWithDesigners: sortedProjects } : prev);
    } catch (err) {
      console.error('Error fetching NPS for ODM projects:', err);
    } finally {
      setOdmDesignersNPSLoading(false);
    }
  };

  const handleCloseODMDesignersModal = () => {
    setODMDesignersModalOpen(false);
    setSelectedODMForDGM(null);
  };

  // ---- Handler for EKOM details modal ----
  const getEkomLeads = (row) => {
    const leads = row.closedLeadsData || [];
    return leads.filter(l => {
      if (l.dateOfDesignKickOffMeeting && l.customerDesignSignOffDate) {
        const diff = (new Date(l.customerDesignSignOffDate) - new Date(l.dateOfDesignKickOffMeeting)) / (1000 * 60 * 60 * 24);
        return diff > 0 && diff < 30;
      }
      return false;
    }).map(l => ({
      ...l,
      ekomDays: Math.round(
        (new Date(l.customerDesignSignOffDate) - new Date(l.dateOfDesignKickOffMeeting)) / (1000 * 60 * 60 * 24)
      )
    }));
  };

  const handleOpenEkomModal = (row) => {
    setSelectedEkomData(row);
    setEkomModalOpen(true);
  };

  const handleCloseEkomModal = () => {
    setEkomModalOpen(false);
    setSelectedEkomData(null);
  };

  // Post Design NPS modal handlers
  const getPostNpsLeads = (row) => {
    return (row.closedLeadsData || [])
      .filter(l => l.postDesignScore !== null && l.postDesignScore !== undefined)
      .sort((a, b) => (b.postDesignScore ?? 0) - (a.postDesignScore ?? 0));
  };

  const handleOpenPostNpsModal = (row) => {
    setSelectedPostNpsData(row);
    setPostNpsModalOpen(true);
  };

  const handleClosePostNpsModal = () => {
    setPostNpsModalOpen(false);
    setSelectedPostNpsData(null);
  };


  const csvHeaders = [
    // { label: "S. No", key: "serialNo" },
    { label: "Name", key: "userName" },
    { label: "Employee Status", key: "status" },
    { label: "Projects", key: "projectsCount" },
    { label: "Ave Value", key: "aveValue" },
    { label: "Month", key: "month" },
    { label: "Total Revenue", key: "totalRevenue" },
    { label: "Achieved (%)", key: "achievedPercentage" },
    { label: "Revenue Achievement", key: "revenueAchievement" },
    { label: "Achievement Points", key: "achievementPoints" },
    { label: "Tenure", key: "tenure" },
    { label: "Tenure Points", key: "tenurePoints" },
    { label: "Achievement + Tenure Points", key: "achievementPlusTenurePoints" },
    { label: "Design NPS", key: "designNPS" },
    { label: "80% Proj / year", key: "eightyPercentageProjectCompleted" },
    { label: "NPS points", key: "npsDesignPoints" },
    { label: "Achievement + Tenure + NPS Points", key: "achievementPlusTenurePlusNPS" },
    { label: "Post Design NPS", key: "postDesignNPS" },
    { label: "Total Points", key: "totalPoints" },
    { label: "Money", key: "payoutAmount" },
  ];
  const formatCurrency = (val) => {
    if (!val && val !== 0) return '0.00';
    return (val / 100000).toFixed(2);
  };
  // totalRevenue is already stored in Lacs (staticDesignWonBudget is pre-divided by 100000)
  const formatRevenue = (val) => {
    if (!val && val !== 0) return '0.00';
    return Number(val).toFixed(2);
  };
  const formatPoints = (val) => {
    if (val == null) return '0';

    if (val >= 1000) {
      const num = val / 1000;

      // remove .0 if whole number
      return Number.isInteger(num) ? `${num}K` : `${num.toFixed(1)}K`;
    }

    return val.toString();
  };

  const formatPointsNoK = (val) => {
    if (val == null) return '0';
    if (val >= 1000) {
      const num = val / 1000;
      return Number.isInteger(num) ? `${num}` : `${num.toFixed(1)}`;
    }
    return val.toString();
  };

  const handleDownload = () => {
    let csvFormattedData;

    if (viewType === 'monthWise' || (showToggle && viewMode === 'myRewards')) {
      // My Rewards / Designer Dashboard - Month-wise data
      csvFormattedData = rewardsData.map((item, index) => {
        return {
          "S. No": index + 1,
          "Month": item.monthLabel || '',
          "Projects Closed": item.projectsCount ?? 0,
          "Target in ₹(Lacs)": formatCurrency(item.targetAmount),
          "Revenue Achieved in ₹(Lacs)": formatRevenue(item.totalRevenue),
          "Achieved (%)": item.achievedPercentage ?? 0,
          "Achievement Points in (Thousands)": formatPointsNoK(item.achievementPoints ?? 0),
          "Tenure": item.tenure || '-',
          "Tenure Points in (Thousands)": formatPointsNoK(item.tenurePoints ?? 0),
          "Achievement + Tenure Points in (Thousands)": formatPointsNoK(item.achievementPlusTenurePoints ?? 0),
          "Design NPS": item.designNPS ?? 0,
          "NPS Points in (Thousands)": formatPointsNoK(item.npsDesignPoints ?? 0),
          "Achievement + Tenure + NPS Points in (Thousands)": formatPointsNoK(item.achievementPlusTenurePlusNPS ?? 0),
          "Post Design NPS": item.postDesignNPS ?? 0,
          "Post Design NPS Points": formatPointsNoK(item.npsPostDesignPoints ?? 0),
          "EKOM to DSO < 30 Days": item.ekomToDso30Days ?? 0,
          "EKOM Points in (Thousands)": formatPointsNoK(item.ekomPoints ?? 0),
          "Total Points in (Thousands)": formatPointsNoK(item.totalPoints ?? 0),
          "Redeem Points in (Thousands)": formatPointsNoK(item.redeemablePoints ?? 0),
          "Eligibility Criteria": item.isTotal
            ? (item.isRedemptionEligible ? "Eligible" : "Not Eligible")
            : ""
        };
      });
    } else {
      // My Team - User-wise data
      csvFormattedData = rewardsData.map((item, index) => {
        return {
          "S. No": index + 1,
          "Name": item.userName || "NA",
          "Employee Status": item.status || "Active",
          "Projects Closed": item.projectsCount ?? 0,
          "Target in ₹(Lacs)": formatCurrency(item.targetAmount),
          "Revenue Achieved in ₹(Lacs)": formatRevenue(item.totalRevenue),
          "Achieved (%)": item.achievedPercentage ?? 0,
          "Achievement Points in (Thousands)": formatPointsNoK(item.achievementPoints ?? 0),
          "Achievement + Tenure Points in (Thousands)": formatPointsNoK(item.achievementPlusTenurePoints ?? 0),
          "Design NPS": (item.npsFiveProjectCount ?? 0) + " Projects",
          "NPS Points in (Thousands)": formatPointsNoK(item.npsDesignPoints ?? (item.npsFiveProjectCount ?? 0) * cfg.npsPointsPerProject),
          "Achievement + Tenure + NPS Points in (Thousands)": formatPointsNoK(item.achievementPlusTenurePlusNPS ?? 0),
          "Post Design NPS": item.postDesignNPS?.toFixed(1) ?? 0,
          "Post Design NPS Points in (Thousands)": formatPointsNoK(item.npsPostDesignPoints ?? 0),
          "EKOM to DSO < 30 Days": item.ekomToDso30Days ?? 0,
          "EKOM Points in (Thousands)": formatPointsNoK(item.ekomPoints ?? 0),
          "Total Points in (Thousands)": formatPointsNoK(item.totalPoints ?? 0),
          "Redeem Points in (Thousands)": formatPointsNoK(item.redeemablePoints ?? 0),
          "Eligibility Criteria": item.isRedemptionEligible ? "Eligible" : "Not Eligible"
        };
      });
    }

    // Convert to CSV and download
    const csvRows = [];
    const headers = Object.keys(csvFormattedData[0] || {});
    csvRows.push(headers.join(','));

    for (const row of csvFormattedData) {
      const values = headers.map(header => {
        const val = row[header];
        return `"${String(val).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(','));
    }

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `erps-${viewMode === 'myRewards' ? 'my-rewards' : 'my-team'}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const shouldShowEmployeeStatusColumn =
    isAdmin ||
    (showToggle && viewMode === 'myTeam');  // Manager + My Team case

  const isDesigner = userRoleNames.includes('Design User');

  return (
    <>
      <Box className={classes.root}>
        {((showToggle && viewMode === 'myRewards') || isDesigner) && (
          <Paper
            style={{
              padding: "2rem",
              borderRadius: "10px",
              boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
              marginBottom: "16px",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  label="From Date"
                  type="date"
                  variant="outlined"
                  value={myRewardsDateFilter?.startDate || ''}
                  onChange={(e) => setMyRewardsDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="End Date"
                  type="date"
                  variant="outlined"
                  value={myRewardsDateFilter?.endDate || ''}
                  onChange={(e) => setMyRewardsDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={handleMyRewardsDateSubmit}
                  style={{ backgroundColor: '#4caf50', color: '#fff', fontWeight: 'bold', textTransform: 'none', padding: '7px 22px' }}
                >
                  SUBMIT
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={() => {
                    setMyRewardsDateFilter({ startDate: '', endDate: '' });
                    onViewModeChange('myRewards');
                  }}
                  style={{ backgroundColor: '#f44336', color: '#fff', fontWeight: 'bold', textTransform: 'none', padding: '7px 22px' }}
                >
                  CLEAR FILTER
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}


        {showToggle && (
          <div style={{
            display: "flex",
            justifyContent: "center",
            gap: "16px",
            margin: "16px 0"
          }}>
            <Button
              variant="contained"
              onClick={() => onViewModeChange('myRewards')}
              style={{
                backgroundColor: viewMode === 'myRewards' ? "#1c375d" : "#9e9e9e",
                color: "#fff",
                fontWeight: "bold",
                padding: "10px 28px",
                fontSize: "15px",
                // border: viewMode === 'myRewards' ? "3px solid #fdb913" : "3px solid transparent",
                boxShadow: viewMode === 'myRewards' ? "0 0 10px rgba(253,185,19,0.6)" : "none",
                transition: "all 0.3s"
              }}
            >
              ⭐ My Rewards
            </Button>
            <Button
              variant="contained"
              onClick={() => onViewModeChange('myTeam')}
              style={{
                backgroundColor: viewMode === 'myTeam' ? "#1c375d" : "#9e9e9e",
                color: "#fff",
                fontWeight: "bold",
                padding: "10px 28px",
                fontSize: "15px",
                // border: viewMode === 'myTeam' ? "3px solid #fdb913" : "3px solid transparent",
                boxShadow: viewMode === 'myTeam' ? "0 0 10px rgba(253,185,19,0.6)" : "none",
                transition: "all 0.3s"
              }}
            >
              👥 My Team
            </Button>

          </div>
        )}

        {/* Target Card - Only for MonthWise & My Rewards (NOT for My Team) */}
        {(viewType === 'monthWise' || (showToggle && viewMode === 'myRewards')) && rewardsData.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', padding: '16px', background: '#f0f4f8', borderRadius: '16px', marginBottom: '20px' }}>

            {/* TOTAL EARNED REWARDS */}
            <Paper style={{ borderRadius: '14px', padding: '20px', flex: '3 1 200px', minWidth: '180px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', background: 'linear-gradient(135deg, #1c375d 0%, #2e5a8a 100%)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '10px', borderBottom: '2px solid #fdb913', marginBottom: '14px', width: '100%', justifyContent: 'center' }}>
                <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#fdb913', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>🏆</div>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#fdb913', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Earned Rewards</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                <Typography style={{ fontSize: '11px', color: '#fdb913', fontWeight: 500, letterSpacing: '0.5px' }}>Total Points Accumulated</Typography>
                <Typography style={{ fontSize: '42px', fontWeight: 800, color: '#fdb913', textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                  {formatPoints(displayedPoints)}
                </Typography>
              </div>
            </Paper>

            {/* MONTHLY POINTS CHART - Only in My Rewards monthWise view */}
            {(viewType === 'monthWise' || (showToggle && viewMode === 'myRewards')) && getMonthlyChartData() && (
              <Paper
                style={{
                  borderRadius: '14px',
                  padding: '20px',
                  flex: '7 1 280px',
                  minWidth: '250px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  background: '#fff'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                }}
                onClick={() => {
                  setMonthlyChartData(getMonthlyChartData());
                  setChartModalOpen(true);
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '10px', borderBottom: '2px solid #fdb913', marginBottom: '14px', width: '100%', justifyContent: 'center' }}>
                  <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#1c375d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>📈</div>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#1c375d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Monthly Trend</span>
                </div>
                <div style={{ width: '100%', height: '200px', position: 'relative' }}>
                  <Line
                    data={getMonthlyChartData()}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top',
                          labels: {
                            color: '#1c375d',
                            font: { size: 11, weight: 'bold' }
                          }
                        },
                        tooltip: {
                          backgroundColor: '#1c375d',
                          padding: 12,
                          titleColor: '#fdb913',
                          bodyColor: '#fff',
                          borderColor: '#fdb913',
                          borderWidth: 2
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          grid: {
                            color: 'rgba(0,0,0,0.05)',
                            drawBorder: false
                          },
                          ticks: {
                            color: '#666',
                            font: { size: 10 }
                          }
                        },
                        x: {
                          grid: {
                            display: false,
                            drawBorder: false
                          },
                          ticks: {
                            color: '#666',
                            font: { size: 10 }
                          }
                        }
                      }
                    }}
                  />
                </div>
                <Typography style={{ fontSize: '10px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>Click to view full chart</Typography>
              </Paper>
            )}


          </div>
        )}

        <div style={{ textAlign: 'right', margin: '10px 0' }}>
          <Button
            variant="contained"
            style={{ backgroundColor: "#355025", color: "#FFF2EB" }}
            onClick={handleDownload}
          >
            <RiFileExcel2Fill style={{ fontSize: "20px", marginRight: '5px' }} />
            Download CSV
          </Button>
        </div>

        <TableContainer component={Paper} style={{ flexGrow: 1, position: "relative", width: '100%' }}>
          {loading && <CustomLoadingOverlay />}

          {!loading && rewardsData.length === 0 && (
            <Box
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                color: "#888",
              }}
            >
              <Typography variant="h6">No Data Found</Typography>
            </Box>
          )}
          <Table stickyHeader size="small" style={{ tableLayout: 'auto', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell colSpan={viewType === 'monthWise' ? 21 : 22}>
                  <TablePagination
                    rowsPerPageOptions={[50, 100, 250, { label: 'All', value: rewardsData.length }]}
                    component="div"
                    count={rewardsData.length}
                    rowsPerPage={rowsPerPage}
                    className={classes.paginationRoot}
                    page={page}
                    SelectProps={{
                      inputProps: { 'aria-label': 'rows per page' },
                      native: true,
                    }}
                    onRowsPerPageChange={handleChangeRowPerPage}
                    onPageChange={handleChangePage}
                    style={{ flexShrink: 0 }}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                {(viewType === 'monthWise'
                  ? ["S. No", "Month", "Projects Closed", "Target in\n₹(Lacs)", "Revenue Achieved\nin ₹(Lacs)", "Achieved\n(%)",
                    "Achievement Points\nin (Thousands)", "Tenure", "Tenure Points\nin (Thousands)", "Achievement +\nTenure Points (Thousands)",
                    "Design NPS", "NPS Points\nin (Thousands)", "Achievement + Tenure\n+ NPS Points (Thousands)",
                    "Post Design NPS", "Post Design\nNPS Points", "EKOM to DSO\n< 30 Days",
                    "EKOM Points", "Total Points\nin (Thousands)", "Actions"]
                  : ["S. No", "Name", ...(shouldShowEmployeeStatusColumn ? ["Employee\nStatus"] : []), "Projects Closed", "Target in\n₹(Lacs)", "Revenue Achieved\nin ₹(Lacs)",
                    "Achieved\n(%)", "Achievement Points\nin (Thousands)", "Tenure", "Tenure Points\nin (Thousands)", "Achievement +\nTenure Points (Thousands)", "Design NPS", "NPS Points\nin (Thousands)",
                    "Achievement + Tenure\n+ NPS Points (Thousands)", "Post Design NPS", "Post Design\nNPS Points (Thousands)",
                    "EKOM to DSO\n< 30 Days", "EKOM Points\nin (Thousands)", "Total Points\nin (Thousands)", "Eligibility\nCriteria", "Action"]
                ).map((header, index) => (
                  <TableCell key={index} className={classes.headerCell}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                      <span style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
                        {header}
                      </span>
                      {COLUMN_TOOLTIPS[header.replace(/\n/g, ' ')] && (
                        <Tooltip title={COLUMN_TOOLTIPS[header.replace(/\n/g, ' ')]} arrow placement="top" classes={{ tooltip: classes.customTooltip }}>
                          <span style={{ cursor: 'pointer', fontSize: '12px', opacity: 0.75, lineHeight: 1 }}>ⓘ</span>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedData.map((row, index) => {
                const isTotal = row.isTotal;
                const rowStyle = isTotal
                  ? { backgroundColor: '#1c375d', color: '#fff', fontWeight: 'bold' }
                  : index % 2 === 0 ? undefined : undefined;

                const cellStyle = isTotal
                  ? { color: '#fff', fontWeight: 'bold', fontSize: '14px', padding: '16px', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.2)' }
                  : {};

                if (viewType === 'monthWise') {
                  return (
                    <TableRow
                      key={index}
                      className={!isTotal ? (index % 2 === 0 ? classes.tableRowEven : classes.tableRowOdd) : ''}
                      style={isTotal ? { backgroundColor: '#1c375d' } : {}}
                    >
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>
                        {isTotal ? '' : page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>
                        <strong>{row.monthLabel}</strong>
                      </TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>
                        <span
                          onClick={() => !isTotal && (row.projectsCount ?? 0) > 0 && (row?.isODMRow ? handleOpenProjectsModal(row) : handleOpenLeadsModal(row))}
                          style={{
                            cursor: (!isTotal && (row.projectsCount ?? 0) > 0) ? 'pointer' : 'default',
                            color: (!isTotal && (row.projectsCount ?? 0) > 0) ? '#1976d2' : 'inherit',
                            textDecoration: (!isTotal && (row.projectsCount ?? 0) > 0) ? 'underline' : 'none',
                            fontWeight: 'bold'
                          }}
                        >
                          {row.projectsCount ?? 0}
                        </span>
                      </TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>{formatCurrency(row.targetAmount)}</TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>{formatRevenue(row.totalRevenue)}</TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>
                        <span style={{
                          color: (row.achievedPercentage === '-' || row.achievedPercentage < 60) ? '#f44336' : '#4caf50',
                          fontWeight: 'bold'
                        }}>
                          {row.achievedPercentage === '-' ? '-' : `${row.achievedPercentage ?? 0}%`}
                        </span>
                      </TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>{formatPointsNoK(row.achievementPoints ?? 0)}</TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>{row.tenure ?? '-'}</TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>{formatPointsNoK(row.tenurePoints ?? 0)}</TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>{formatPointsNoK(row.achievementPlusTenurePoints ?? 0)}</TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>
                        <span
                          onClick={() => !isTotal && (row.projectsCount ?? 0) > 0 && (row?.isODMRow ? handleOpenProjectsModal(row) : handleOpenLeadsModal(row))}
                          style={{
                            cursor: (!isTotal && (row.projectsCount ?? 0) > 0) ? 'pointer' : 'default',
                            color: (!isTotal && (row.projectsCount ?? 0) > 0) ? '#1976d2' : 'inherit',
                            textDecoration: (!isTotal && (row.projectsCount ?? 0) > 0) ? 'underline' : 'none',
                            fontWeight: 'bold'
                          }}
                        >
                          {row.designNPS !== null && row.designNPS !== undefined ? row.designNPS.toFixed(1) : '-'}
                        </span>
                      </TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>{formatPointsNoK(row.npsDesignPoints ?? 0)}</TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>{formatPointsNoK(row.achievementPlusTenurePlusNPS ?? 0)}</TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>
                        <span
                          onClick={() => !isTotal && row.postDesignNPS != null && handleOpenPostNpsModal(row)}
                          style={{
                            cursor: (!isTotal && row.postDesignNPS != null) ? 'pointer' : 'default',
                            color: (!isTotal && row.postDesignNPS != null) ? '#1976d2' : 'inherit',
                            textDecoration: (!isTotal && row.postDesignNPS != null) ? 'underline' : 'none',
                            fontWeight: 'bold'
                          }}
                        >
                          {row.postDesignNPS !== null && row.postDesignNPS !== undefined ? row.postDesignNPS.toFixed(1) : '-'}
                        </span>
                      </TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>{isTotal ? formatPointsNoK(row.npsPostDesignPoints ?? 0) : '-'}</TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>
                        <span
                          onClick={() => !isTotal && (row.ekomToDso30Days ?? 0) > 0 && handleOpenEkomModal(row)}
                          style={{
                            cursor: (!isTotal && (row.ekomToDso30Days ?? 0) > 0) ? 'pointer' : 'default',
                            color: (!isTotal && (row.ekomToDso30Days ?? 0) > 0) ? '#1976d2' : 'inherit',
                            textDecoration: (!isTotal && (row.ekomToDso30Days ?? 0) > 0) ? 'underline' : 'none',
                            fontWeight: 'bold'
                          }}
                        >
                          {row.ekomToDso30Days ?? 0}
                        </span>
                      </TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>{formatPointsNoK(row.ekomPoints ?? 0)}</TableCell>
                      <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}><strong>{formatPointsNoK(row.totalPoints ?? 0)}</strong></TableCell>
                      {/* <TableCell className={classes.cell} style={isTotal ? cellStyle : {}}>{formatPointsNoK(row.redeemablePoints ?? 0)}</TableCell> */}
                      {/* Actions column — status badge + redeem button */}
                      {index === 0 && (
                        <TableCell
                          rowSpan={paginatedData.length}
                          className={classes.cell}
                          style={{
                            padding: '6px 4px',
                            minWidth: 120,
                            verticalAlign: 'middle',
                            borderLeft: '1px solid #e0e0e0'
                          }}
                        >
                          {/* Top half - Eligible */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingBottom: '20px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{
                                backgroundColor: rewardsData.find(r => r.isTotal)?.isRedemptionEligible ? '#4caf50' : '#f44336',
                                color: '#fff',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '11px',
                                fontWeight: 'bold'
                              }}>
                                {rewardsData.find(r => r.isTotal)?.isRedemptionEligible ? 'Eligible' : 'Not Eligible'}
                              </span>
                              <span
                                onClick={() => { setSelectedEligibilityRow(rewardsData.find(r => r.isTotal)); setEligibilityCriteriaModalOpen(true); }}
                                style={{ cursor: 'pointer', fontSize: '15px', color: '#1c375d' }}
                              >ⓘ</span>
                            </div>
                          </div>

                          {/* Bottom half - Redeem */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingTop: '20px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <button disabled style={{
                                backgroundColor: '#9e9e9e',
                                color: '#fff',
                                border: 'none',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                cursor: 'not-allowed',
                                opacity: 0.7
                              }}>Redeem</button>
                              <span
                                onClick={() => setRedemptionInfoModalOpen(true)}
                                style={{ cursor: 'pointer', fontSize: '15px', color: '#1c375d' }}
                              >ⓘ</span>
                            </div>
                          </div>
                        </TableCell>
                      )}
                      {/* <TableCell className={classes.cell}>
        <MdOutlineSettings
          className={classes.rotateIcon}
          style={{ fontSize: "25px", cursor: "pointer", color: "#3eaa04ff" }}
          onClick={() => { setSelectedRemarks(row); setOpen(true); }}
        />
      </TableCell> */}
                    </TableRow>
                  );
                }

                // Original team view - Skip manager's own row in My Team
                if (showToggle && viewMode === 'myTeam' && row._id === userDetails?._id) {
                  return null;
                }

                // Original team view
                return (
                  <TableRow
                    key={index}
                    className={index % 2 === 0 ? classes.tableRowEven : classes.tableRowOdd}
                  >
                    <TableCell className={classes.cell}>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell className={classes.cell}>{row.userName || 'N/A'}</TableCell>
                    {shouldShowEmployeeStatusColumn && (
                      <TableCell className={classes.cell}>{row.status || 'Active'}</TableCell>
                    )}
                    <TableCell className={classes.cell}>
                      <span
                        onClick={() => {
                          const count = row.projectsClosed ?? row.projectsCount ?? 0;
                          if (count <= 0) return;
                          if (row.isAGMRow) return handleOpenAGMODMsModal(row);
                          if (row.isODMRow) return handleOpenProjectsModal(row);
                          handleOpenLeadsModal(row);
                        }}
                        style={{
                          cursor: (row.projectsClosed ?? row.projectsCount ?? 0) > 0 ? 'pointer' : 'default',
                          color: (row.projectsClosed ?? row.projectsCount ?? 0) > 0 ? '#1976d2' : 'inherit',
                          textDecoration: (row.projectsClosed ?? row.projectsCount ?? 0) > 0 ? 'underline' : 'none',
                          fontWeight: 'bold'
                        }}
                      >
                        {row.projectsClosed ?? row.projectsCount ?? 0}
                      </span>
                    </TableCell>
                    <TableCell className={classes.cell}>{formatCurrency(row.targetAmount ?? row.target)}</TableCell>
                    <TableCell className={classes.cell}>{formatRevenue(row.totalRevenue)}</TableCell>
                    <TableCell className={classes.cell}>
                      <span style={{
                        color: (row.achievedPercentage ?? 0) < 60 ? '#f44336' : '#4caf50',
                        fontWeight: 'bold'
                      }}>
                        {row.achievedPercentage ?? 0}%
                      </span>
                    </TableCell>
                    <TableCell className={classes.cell}>{formatPointsNoK(row.achievementPoints ?? 0)}</TableCell>
                    <TableCell className={classes.cell}>{row.tenure || 'N/A'}</TableCell>
                    <TableCell className={classes.cell}>{formatPointsNoK(row.tenurePoints ?? 0)}</TableCell>
                    <TableCell className={classes.cell}>{formatPointsNoK(row.achievementPlusTenurePoints ?? 0)}</TableCell>
                    <TableCell className={classes.cell}>
                      <span
                        onClick={() => {
                          if ((row.projectsClosed ?? row.projectsCount ?? 0) > 0) {
                            handleOpenProjectsModal(row, true);
                          }
                        }}
                        style={{
                          cursor: (row.projectsClosed ?? row.projectsCount ?? 0) > 0 ? 'pointer' : 'default',
                          color: (row.projectsClosed ?? row.projectsCount ?? 0) > 0 ? '#1976d2' : 'inherit',
                          textDecoration: (row.projectsClosed ?? row.projectsCount ?? 0) > 0 ? 'underline' : 'none',
                          fontWeight: 'bold'
                        }}
                      >
                        {row.npsFiveProjectCount ?? 0}
                      </span>
                    </TableCell>
                    <TableCell className={classes.cell}>{formatPointsNoK(row.npsDesignPoints ?? (row.npsFiveProjectCount ?? 0) * cfg.npsPointsPerProject)}</TableCell>
                    <TableCell className={classes.cell}>{formatPointsNoK(row.achievementPlusTenurePlusNPS ?? 0)}</TableCell>
                    <TableCell className={classes.cell}>
                      <span
                        onClick={() => row.postDesignNPS != null && handleOpenPostNpsModal(row)}
                        style={{
                          cursor: row.postDesignNPS != null ? 'pointer' : 'default',
                          color: row.postDesignNPS != null ? '#1976d2' : 'inherit',
                          textDecoration: row.postDesignNPS != null ? 'underline' : 'none',
                          fontWeight: 'bold'
                        }}
                      >
                        {row.postDesignNPS?.toFixed(1) ?? '-'}
                      </span>
                    </TableCell>
                    <TableCell className={classes.cell}>{formatPointsNoK(row.npsPostDesignPoints ?? 0)}</TableCell>
                    <TableCell className={classes.cell}>
                      <span
                        onClick={() => (row.ekomToDso30Days ?? 0) > 0 && handleOpenEkomModal(row)}
                        style={{
                          cursor: (row.ekomToDso30Days ?? 0) > 0 ? 'pointer' : 'default',
                          color: (row.ekomToDso30Days ?? 0) > 0 ? '#1976d2' : 'inherit',
                          textDecoration: (row.ekomToDso30Days ?? 0) > 0 ? 'underline' : 'none',
                          fontWeight: 'bold'
                        }}
                      >
                        {row.ekomToDso30Days ?? 0}
                      </span>
                    </TableCell>
                    <TableCell className={classes.cell}>{formatPointsNoK(row.ekomPoints ?? 0)}</TableCell>
                    <TableCell className={classes.cell}><strong>{formatPointsNoK(row.totalPoints ?? 0)}</strong></TableCell>
                    {/* <TableCell className={classes.cell}>{formatPointsNoK(row.redeemablePoints ?? 0)}</TableCell> */}
                    <TableCell className={classes.cell}>
                      {row?.isRedemptionEligible ? (
                        <Tooltip
                          title={
                            <div style={{ padding: '8px' }}>
                              <strong>✅ Eligible for Redemption</strong>
                              <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
                                <li>{(row?.consecutiveEligibleMonths || 0) >= cfg.minConsecutiveMonths ? '✅' : '❌'} {row?.consecutiveEligibleMonths || 0} consecutive months with ≥{cfg.minAchievementPct}% achievement ({cfg.minConsecutiveMonths} are Required)</li>
                                <li>{(row?.achievedPercentage || 0) >= cfg.minAchievementPct ? '✅' : '❌'} Year-end cumulative achievement: {row?.achievedPercentage || 0}% (Need ≥{cfg.minAchievementPct}%)</li>
                                {!row?.statusBlockReason && <li>✅ Active status (No PIP/Refresher/Notice Period)</li>}
                                <li>📅 Redemption window: December only</li>
                              </ul>
                            </div>
                          }
                          arrow
                          placement="bottom"
                          classes={{ tooltip: classes.customTooltip }}
                        >
                          <span style={{
                            backgroundColor: '#4caf50',
                            color: '#fff',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            display: 'inline-block',
                            cursor: 'pointer'
                          }}>Eligible
                            <span style={{ fontSize: '13px', opacity: 0.85 }}> ⓘ</span>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={
                            <div style={{ padding: '8px' }}>
                              <strong>❌ Not Eligible for Redemption</strong>
                              <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
                                {(row?.consecutiveEligibleMonths || 0) < cfg.minConsecutiveMonths && (
                                  <li>❌ Need {cfg.minConsecutiveMonths - (row?.consecutiveEligibleMonths || 0)} more consecutive month(s) with ≥{cfg.minAchievementPct}% achievement (Current: {row?.consecutiveEligibleMonths || 0})</li>
                                )}
                                {(row?.achievedPercentage || 0) < cfg.minAchievementPct && (row?.consecutiveEligibleMonths || 0) >= cfg.minConsecutiveMonths && (
                                  <li>❌ Year-end cumulative achievement is {(row?.achievedPercentage || 0)}%, need ≥{cfg.minAchievementPct}%</li>
                                )}
                                {row?.statusBlockReason && (
                                  <li>❌ Status issue: {row?.statusBlockReason}</li>
                                )}
                                {!row?.statusBlockReason && (row?.consecutiveEligibleMonths || 0) >= cfg.minConsecutiveMonths && (row?.achievedPercentage || 0) >= cfg.minAchievementPct && (
                                  <li>📅 Redemption window opens in December only</li>
                                )}
                                {(row?.consecutiveEligibleMonths || 0) < cfg.minConsecutiveMonths && !row?.statusBlockReason && (row?.achievedPercentage || 0) >= cfg.minAchievementPct && (
                                  <li>📅 Redemption window opens in December only</li>
                                )}
                              </ul>
                            </div>
                          }
                          arrow
                          placement="bottom"
                          classes={{ tooltip: classes.customTooltip }}
                        >
                          <span style={{
                            backgroundColor: '#f44336',
                            color: '#fff',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            display: 'inline-block',
                            cursor: 'pointer'
                          }}>Not Eligible
                            <span style={{ fontSize: '13px', opacity: 0.85 }}> ⓘ</span>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                        {/* Settings Icon */}
                        <MdOutlineSettings
                          className={classes.rotateIcon}
                          style={{ fontSize: "20px", cursor: "pointer", color: "#4caf50" }}
                          onClick={() => { setSelectedRemarks(row); setOpen(true); }}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {rewardsData.length > 0 && (
          <>
            <br />
            {/* ELIGIBILITY CRITERIA - above notes */}
            <div style={{ marginTop: '12px', padding: '10px 16px', background: '#eef2f8', borderLeft: '3px solid #1c375d', borderRadius: '4px', textAlign: 'left' }}>
              <Typography style={{ fontSize: '11px', fontWeight: 700, color: '#1c375d', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px' }}>🛡️ Eligibility Criteria</Typography>
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                <li><Typography style={{ fontSize: '11px', lineHeight: 1.8, color: '#333' }}><strong>{cfg.minAchievementPct}% of monthly target for {cfg.minConsecutiveMonths} consecutive months</strong> required to be eligible for redemption.</Typography></li>
                <li><Typography style={{ fontSize: '11px', lineHeight: 1.8, color: '#333' }}>Year-end <strong>cumulative achievement must exceed {cfg.minAchievementPct}%</strong> to qualify.</Typography></li>
              </ol>
            </div>

            {/* NOTES */}
            <div style={{ marginTop: '20px', padding: '10px 16px 10px 0px', background: '#fff8e1', borderLeft: '3px solid #fdb913', borderRadius: '4px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <Typography style={{ fontSize: '11px', fontWeight: 700, color: '#1c375d', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>&nbsp;&nbsp; 📝 Note:</Typography>
              <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', gap: '24px', flexWrap: 'wrap', listStyle: 'disc' }}>
                {/* <li><Typography style={{ fontSize: '11px', lineHeight: 1.6, color: '#555' }}>Minimum 6 months tenure required to be eligible.</Typography></li> */}
                <li><Typography style={{ fontSize: '11px', lineHeight: 1.6, color: '#555' }}>HR warning or PIP — Employees won't be able to redeem points.</Typography></li>
              </ul>
            </div>
          </>
        )}
      </Box>

      {/* Redemption disclaimer component */}
      {(() => {
        const RedemptionDisclaimer = () => (
          <Tooltip
            title={
              <div style={{ padding: '8px' }}>
                <strong>Redemption Eligibility Criteria:</strong>
                <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
                  <li>✓ {cfg.minConsecutiveMonths} consecutive months with ≥{cfg.minAchievementPct}% target achievement</li>
                  <li>✓ Year-end cumulative achievement ≥{cfg.minAchievementPct}%</li>
                  <li>✓ Not on PIP/Refresher/Notice Period</li>
                </ul>
              </div>
            }
            arrow
            placement="bottom"
          >
            <span style={{ cursor: 'pointer', marginLeft: '8px', color: '#fdb913', fontSize: '16px' }}>ⓘ</span>
          </Tooltip>
        );
        return <RedemptionDisclaimer />;
      })()}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle style={{ backgroundColor: '#1c375d', color: '#fff' }}>
          🎯 Redemption Eligibility
        </DialogTitle>
        <DialogContent dividers>
          {selectedRemarks && (
            <>
              {/* User Info */}
              <Box mb={2}>
                <Typography variant="h6">{selectedRemarks.userName}</Typography>
                <Typography variant="body2">
                  Total Points: <strong>{formatPoints(selectedRemarks.totalPoints || 0)}</strong>
                </Typography>
              </Box>

              {/* Eligibility Criteria Checklist */}
              <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: '16px' }}>
                ✅ Eligibility Criteria
              </Typography>

              <Grid container spacing={2} style={{ marginTop: '8px' }}>
                {/* Criteria 1: 3 consecutive months with ≥80% */}
                <Grid item xs={12}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {selectedRemarks.consecutiveEligibleMonths >= cfg.minConsecutiveMonths ? (
                      <span style={{ color: '#4caf50', fontSize: '24px' }}>✅</span>
                    ) : (
                      <span style={{ color: '#f44336', fontSize: '24px' }}>❌</span>
                    )}
                    <div>
                      <strong>{cfg.minConsecutiveMonths} consecutive months with ≥{cfg.minAchievementPct}% achievement</strong>
                      <Typography variant="caption" display="block" color="textSecondary">
                        Current: {selectedRemarks.consecutiveEligibleMonths || 0} Done, ({cfg.minConsecutiveMonths} months required)
                      </Typography>
                    </div>
                  </div>
                </Grid>

                {/* Criteria 2: Year-end cumulative ≥80% */}
                <Grid item xs={12}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {(selectedRemarks.achievedPercentage || 0) >= cfg.minAchievementPct ? (
                      <span style={{ color: '#4caf50', fontSize: '24px' }}>✅</span>
                    ) : (
                      <span style={{ color: '#f44336', fontSize: '24px' }}>❌</span>
                    )}
                    <div>
                      <strong>Year-end cumulative achievement ≥{cfg.minAchievementPct}%</strong>
                      <Typography variant="caption" display="block" color="textSecondary">
                        Current: {selectedRemarks.achievedPercentage || 0}%
                      </Typography>
                    </div>
                  </div>
                </Grid>

                {/* Criteria 3: Employee Status */}
                <Grid item xs={12}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {!selectedRemarks.statusBlockReason ? (
                      <span style={{ color: '#4caf50', fontSize: '24px' }}>✅</span>
                    ) : (
                      <span style={{ color: '#f44336', fontSize: '24px' }}>❌</span>
                    )}
                    <div>
                      <strong>Active Status (No PIP/Refresher/Notice Period)</strong>
                      <Typography variant="caption" display="block" color="textSecondary">
                        Status: {selectedRemarks.status || 'Active'}
                        {selectedRemarks.statusBlockReason && (
                          <span style={{ color: '#f44336' }}> - {selectedRemarks.statusBlockReason}</span>
                        )}
                      </Typography>
                    </div>
                  </div>
                </Grid>
              </Grid>

              {/* Disclaimer if not eligible */}
              {!selectedRemarks.isRedemptionEligible && (
                <Alert severity="warning" style={{ marginTop: '16px' }}>
                  <strong>Not Eligible for Redemption</strong>
                  <ul style={{ margin: '8px 0 0 20px' }}>
                    {(selectedRemarks.consecutiveEligibleMonths || 0) < cfg.minConsecutiveMonths && (
                      <li>Need {cfg.minConsecutiveMonths - (selectedRemarks.consecutiveEligibleMonths || 0)} more consecutive month(s) with ≥{cfg.minAchievementPct}% achievement</li>
                    )}
                    {(selectedRemarks.achievedPercentage || 0) < cfg.minAchievementPct && (
                      <li>Year-end cumulative achievement is {(selectedRemarks.achievedPercentage || 0)}%, need {cfg.minAchievementPct}%</li>
                    )}
                    {selectedRemarks.statusBlockReason && (
                      <li>Status issue: {selectedRemarks.statusBlockReason}</li>
                    )}
                  </ul>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={leadsModalOpen} onClose={handleCloseLeadsModal} maxWidth="md" fullWidth>
        <DialogTitle>
          Closed Projects — {selectedMonthData?.userName || 'N/A'}
        </DialogTitle>
        <DialogContent dividers style={{ minHeight: '300px' }}>
          {leadsLoading ? (
            <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <Typography>Loading...</Typography>
            </Box>
          ) : leadsData.length === 0 ? (
            <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <Typography>No leads found</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow style={{ backgroundColor: '#1c375d' }}>
                  <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>S. No</TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }}>Lead No</TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>Customer Name</TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }}>IDSO Date</TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }}>NPS Score</TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }}>Revenue (in Lacs)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leadsData.map((lead, index) => (
                  <TableRow key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f0f4f8' }}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <a
                        href={`/project-logs/${lead._id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1c375d', textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        {lead.lead_no || lead._id}
                      </a>
                    </TableCell>
                    <TableCell style={{ maxWidth: 160, wordBreak: 'break-word' }}>{lead.customerName || 'N/A'}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {lead.internalDesignSignOffDate
                        ? moment(lead.internalDesignSignOffDate).format('DD MMM YYYY')
                        : 'N/A'}
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {lead.npsScore === 5 ? (
                        <span style={{ color: '#4caf50', fontWeight: 'bold' }}>⭐ {lead.npsScore}/5</span>
                      ) : lead.npsScore && lead.npsScore !== 'Not Available' ? (
                        <span style={{ color: '#ff9800' }}>{lead.npsScore}/5</span>
                      ) : (
                        <span style={{ color: '#999' }}>N/A</span>
                      )}
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {lead.staticDesignWonBudget ? `₹${lead.staticDesignWonBudget}` : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLeadsModal} color="primary" variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* ---- EKOM to DSO < 30 Days Details Modal ---- */}
      <Dialog open={ekomModalOpen} onClose={handleCloseEkomModal} maxWidth="md" fullWidth>
        <DialogTitle style={{ backgroundColor: '#1c375d', color: '#fff' }}>
          ⚡ EKOM to DSO &lt; 30 Days — {selectedEkomData?.userName || selectedEkomData?.monthLabel || 'N/A'}
          {/* {selectedEkomData?.month && selectedEkomData?.year
            ? ` (${moment(`${selectedEkomData.year}-${String(selectedEkomData.month).padStart(2, '0')}`).format('MMMM YYYY')})`
            : ''} */}
        </DialogTitle>
        <DialogContent dividers style={{ minHeight: '300px' }}>
          {(() => {
            const ekomLeads = selectedEkomData ? getEkomLeads(selectedEkomData) : [];
            return ekomLeads.length === 0 ? (
              <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <Typography>No EKOM qualifying projects found</Typography>
              </Box>
            ) : (
              <>
                <Box mb={2} style={{ padding: '12px 16px', background: '#f0f4f8', borderRadius: '8px', display: 'flex', gap: '24px' }}>
                  <Typography variant="body2">
                    <strong style={{ color: '#1c375d' }}>Qualifying Projects:</strong> {ekomLeads.length}
                  </Typography>
                  <Typography variant="body2">
                    <strong style={{ color: '#1c375d' }}>EKOM Points Earned:</strong> {ekomLeads.length.toLocaleString('en-IN')} × {cfg.ekomPointsPerProject.toLocaleString('en-IN')} = {(ekomLeads.length * cfg.ekomPointsPerProject).toLocaleString('en-IN')} pts
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#1c375d' }}>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>S. No</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>Lead No</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>Customer Name</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }} align="center">EKOM Date</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }} align="center">DSO Date</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }} align="center">Days Taken</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }} align="right">Revenue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ekomLeads.map((lead, index) => (
                      <TableRow key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f0f4f8' }}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <a
                            href={`/project-logs/${lead._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1c375d', textDecoration: 'underline', cursor: 'pointer' }}
                          >
                            {lead.lead_no || lead._id}
                          </a>
                        </TableCell>
                        <TableCell style={{ maxWidth: 160, wordBreak: 'break-word' }}>{lead.customerName || 'N/A'}</TableCell>
                        <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                          {lead.dateOfDesignKickOffMeeting
                            ? moment(lead.dateOfDesignKickOffMeeting).format('DD MMM YYYY')
                            : 'N/A'}
                        </TableCell>
                        <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                          {lead.customerDesignSignOffDate
                            ? moment(lead.customerDesignSignOffDate).format('DD MMM YYYY')
                            : 'N/A'}
                        </TableCell>
                        <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                          <span style={{
                            backgroundColor: lead.ekomDays <= 15 ? '#e8f5e9' : '#fff3e0',
                            color: lead.ekomDays <= 15 ? '#2e7d32' : '#e65100',
                            fontWeight: 'bold',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}>
                            {lead.ekomDays} days
                          </span>
                        </TableCell>
                        <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                          {lead.staticDesignWonBudget ? `₹${lead.staticDesignWonBudget}` : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEkomModal} color="primary" variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* ---- Post Design NPS Modal ---- */}
      <Dialog open={postNpsModalOpen} onClose={handleClosePostNpsModal} maxWidth="md" fullWidth>
        <DialogTitle style={{ backgroundColor: '#1c375d', color: '#fff' }}>
          📊 Post Design NPS — {selectedPostNpsData?.userName || selectedPostNpsData?.monthLabel || 'N/A'}
        </DialogTitle>
        <DialogContent dividers style={{ minHeight: '300px' }}>
          {(() => {
            const postNpsLeads = selectedPostNpsData ? getPostNpsLeads(selectedPostNpsData) : [];
            const avgScore = postNpsLeads.length > 0
              ? (postNpsLeads.reduce((s, l) => s + l.postDesignScore, 0) / postNpsLeads.length).toFixed(2)
              : null;
            return postNpsLeads.length === 0 ? (
              <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <Typography>No Post Design NPS data found</Typography>
              </Box>
            ) : (
              <>
                <Box mb={2} style={{ padding: '12px 16px', background: '#f0f4f8', borderRadius: '8px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                  <Typography variant="body2">
                    <strong style={{ color: '#1c375d' }}>Projects with Survey:</strong> {postNpsLeads.length}
                  </Typography>
                  <Typography variant="body2">
                    <strong style={{ color: '#1c375d' }}>Average Post Design NPS:</strong>{' '}
                    <span style={{ color: avgScore >= cfg.postNpsRequiredScore ? '#4caf50' : avgScore >= 3.5 ? '#ff9800' : '#f44336', fontWeight: 'bold' }}>
                      {avgScore} / 5
                    </span>
                  </Typography>
                  {Number(avgScore) >= cfg.postNpsRequiredScore && (
                    <Typography variant="body2" style={{ color: '#4caf50', fontWeight: 'bold' }}>
                      ✅ Eligible for {cfg.postNpsPoints.toLocaleString('en-IN')} pts bonus
                    </Typography>
                  )}
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#1c375d' }}>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>S. No</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>Lead No</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>Customer Name</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }}>IDSO Date</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }} align="center">Post Design Score</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }} align="right">Revenue (Lacs)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {postNpsLeads.map((lead, index) => (
                      <TableRow key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f0f4f8' }}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <a href={`/project-logs/${lead._id}`} target="_blank" rel="noopener noreferrer"
                            style={{ color: '#1c375d', textDecoration: 'underline', cursor: 'pointer' }}>
                            {lead.lead_no || lead._id}
                          </a>
                        </TableCell>
                        <TableCell style={{ maxWidth: 160, wordBreak: 'break-word' }}>{lead.customerName || 'N/A'}</TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>
                          {lead.internalDesignSignOffDate
                            ? moment(lead.internalDesignSignOffDate).format('DD MMM YYYY')
                            : 'N/A'}
                        </TableCell>
                        <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                          <span style={{
                            color: lead.postDesignScore >= cfg.postNpsRequiredScore ? '#4caf50' : lead.postDesignScore >= 3.5 ? '#ff9800' : '#f44336',
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>
                            {lead.postDesignScore.toFixed(2)} / 5
                          </span>
                        </TableCell>
                        <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                          {lead.staticDesignWonBudget ? `₹${lead.staticDesignWonBudget}` : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePostNpsModal} color="primary" variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* ---- DGM/Admin: AGM row -> ODMs modal (Level 1 drill-down) ---- */}
      <Dialog open={agmODMsModalOpen} onClose={handleCloseAGMODMsModal} maxWidth="lg" fullWidth>
        <DialogTitle style={{ backgroundColor: '#1c375d', color: '#fff' }}>
          ODMs Under AGM — {selectedAGMData?.userName || 'N/A'}
        </DialogTitle>
        <DialogContent dividers style={{ minHeight: '400px' }}>
          {!selectedAGMData ? (
            <Typography>No data</Typography>
          ) : (
            <>
              <Typography variant="h6" style={{ marginBottom: '15px', fontWeight: 'bold', color: '#1c375d' }}>
                Operation Design Managers reporting to {selectedAGMData.userName}
              </Typography>
              {(!selectedAGMData.odmsUnderAGM || selectedAGMData.odmsUnderAGM.length === 0) ? (
                <Typography>No ODMs found</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#1c375d' }}>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>S. No</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>ODM Name</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>Email</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }} align="center">Projects Closed</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }} align="right">Target</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }} align="right">Revenue</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }} align="center">Achieved %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedAGMData.odmsUnderAGM.map((odm, index) => (
                      <TableRow key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f0f4f8' }}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell><strong>{odm.name}</strong></TableCell>
                        <TableCell>{odm.email || 'N/A'}</TableCell>
                        <TableCell align="center">
                          <span
                            onClick={() => odm.projectsCount > 0 && handleOpenODMDesignersModal(odm)}
                            style={{
                              cursor: odm.projectsCount > 0 ? 'pointer' : 'default',
                              color: odm.projectsCount > 0 ? '#1976d2' : 'inherit',
                              textDecoration: odm.projectsCount > 0 ? 'underline' : 'none',
                              fontWeight: 'bold'
                            }}
                          >
                            {odm.projectsCount || 0}
                          </span>
                        </TableCell>
                        <TableCell align="right">₹{Number((odm.targetAmount || 0) / 100000).toFixed(2)} L</TableCell>
                        <TableCell align="right">₹{Number(odm.totalRevenue || 0).toFixed(2)} L</TableCell>
                        <TableCell align="center">
                          <span style={{ color: (odm.achievedPercentage || 0) >= cfg.minAchievementPct ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>
                            {odm.targetAmount > 0 ? `${odm.achievedPercentage || 0}%` : 'N/A'}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAGMODMsModal} color="primary" variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* ---- DGM/Admin: ODM row inside AGM modal -> Designers modal (Level 2 drill-down) ---- */}
      <Dialog open={odmDesignersModalOpen} onClose={handleCloseODMDesignersModal} maxWidth="lg" fullWidth>
        <DialogTitle style={{ backgroundColor: '#1c375d', color: '#fff' }}>
          Projects & Designers — {selectedODMForDGM?.name || 'N/A'}
        </DialogTitle>
        <DialogContent dividers style={{ minHeight: '500px' }}>
          {!selectedODMForDGM ? (
            <Typography>No data</Typography>
          ) : (
            <>

              {/* Section 2: Projects closed by this ODM's team */}
              <div>
                <Typography variant="h6" style={{ marginBottom: '15px', fontWeight: 'bold', color: '#1c375d' }}>
                  Projects Closed
                </Typography>
                {!selectedODMForDGM?.projectsWithDesigners || selectedODMForDGM.projectsWithDesigners.length === 0 ? (
                  <Typography>No projects found</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow style={{ backgroundColor: '#1c375d' }}>
                        <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>S. No</TableCell>
                        <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }}>Project No</TableCell>
                        <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>Customer Name</TableCell>
                        <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>Designer</TableCell>
                        <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }} align="center">NPS Score</TableCell>
                        {/* <TableCell style={{ fontWeight: 'bold', color: '#fff' }} align="center">Sign Off Date</TableCell> */}
                        <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }} align="right">Project Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedODMForDGM.projectsWithDesigners.map((project, index) => (
                        <TableRow key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f0f4f8' }}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <a
                              href={`/project-logs/${project.leadId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#1c375d', textDecoration: 'underline', cursor: 'pointer' }}
                            >
                              {project.leadNo || project.leadId}
                            </a>
                          </TableCell>
                          <TableCell style={{ maxWidth: 150, wordBreak: 'break-word' }}>{project.customerName || 'N/A'}</TableCell>
                          <TableCell><strong>{project.designerName}</strong></TableCell>
                          <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                            {odmDesignersNPSLoading ? (
                              <span style={{ color: '#999', fontSize: '11px' }}>Loading...</span>
                            ) : project.npsScore === 5 ? (
                              <span style={{ color: '#4caf50', fontWeight: 'bold' }}>⭐ {project.npsScore}/5</span>
                            ) : project.npsScore && project.npsScore !== 'N/A' ? (
                              <span style={{ color: '#ff9800', fontWeight: 'bold' }}>{project.npsScore}/5</span>
                            ) : (
                              <span style={{ color: '#999' }}>N/A</span>
                            )}
                          </TableCell>
                          <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>₹{Number(project.revenue || 0).toLocaleString('en-IN')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              <hr style={{ margin: '20px 0', borderColor: '#ddd' }} />

              {/* Section 1: Designers under this ODM */}
              <div style={{ marginBottom: '30px' }}>
                <Typography variant="h6" style={{ marginBottom: '15px', fontWeight: 'bold', color: '#1c375d' }}>
                  Designers Under {selectedODMForDGM.name}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#1c375d' }}>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>S. No</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>Designer Name</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>Email</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }} align="center">Projects</TableCell>
                      <TableCell style={{ fontWeight: 'bold', color: '#fff' }} align="right">Total Revenue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedODMForDGM?.designerDetails?.length > 0 ? (
                      selectedODMForDGM.designerDetails.map((designer, index) => (
                        <TableRow key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f0f4f8' }}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell><strong>{designer.name}</strong></TableCell>
                          <TableCell>{designer.email || 'N/A'}</TableCell>
                          <TableCell align="center">{designer.projectsCount || 0}</TableCell>
                          <TableCell align="right">₹{Number(designer.totalRevenue || 0).toLocaleString('en-IN')}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow><TableCell colSpan="5" style={{ textAlign: 'center' }}>No designers found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>


            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseODMDesignersModal} color="primary" variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* ---- AGM: ODM projects/designers modal ---- */}
      <Dialog open={projectsModalOpen} onClose={handleCloseProjectsModal} maxWidth="lg" fullWidth>
        <DialogTitle>
          Projects & Designers Details — {selectedODMData?.userName || 'N/A'}
        </DialogTitle>
        <DialogContent dividers style={{ minHeight: '600px' }}>
          {!selectedODMData ? (
            <Typography>No data</Typography>
          ) : (
            <>

              {/* Section 2: Projects Closed */}
              <div>
                <Typography variant="h6" style={{ marginBottom: '15px', fontWeight: 'bold', color: '#1c375d' }}>
                  Projects Closed
                </Typography>
                {!selectedODMData?.projectsWithDesigners || selectedODMData.projectsWithDesigners.length === 0 ? (
                  <Typography>No projects found</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow style={{ backgroundColor: '#1c375d' }}>
                        <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>S. No</TableCell>
                        <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }}>Project No</TableCell>
                        <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>Customer Name</TableCell>
                        <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>Designer</TableCell>
                        <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }} align="center">IDSO Date</TableCell>
                        <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }} align="center">NPS Score</TableCell>
                        <TableCell style={{ fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }} align="right">Project Value (Lacs)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedODMData.projectsWithDesigners.map((project, index) => (
                        <TableRow key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f0f4f8' }}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <a
                              href={`/project-logs/${project.leadId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#1c375d', textDecoration: 'underline', cursor: 'pointer' }}
                            >
                              {project.leadNo || project.leadId}
                            </a>
                          </TableCell>
                          <TableCell style={{ maxWidth: 150, wordBreak: 'break-word' }}>{project.customerName || 'N/A'}</TableCell>
                          <TableCell><strong>{project.designerName}</strong></TableCell>
                          <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                            {(project.dsoDate || project.internalDesignSignOffDate)
                              ? moment(project.dsoDate || project.internalDesignSignOffDate).format('DD MMM YYYY')
                              : 'N/A'}
                          </TableCell>
                          <TableCell align="center">
                            {projectsModalNPSLoading ? (
                              <span style={{ color: '#999', fontSize: '11px' }}>Loading...</span>
                            ) : project.npsScore === 5 ? (
                              <span style={{ color: '#4caf50', fontWeight: 'bold' }}>⭐ {project.npsScore}/5</span>
                            ) : project.npsScore && project.npsScore !== 'N/A' ? (
                              <span style={{ color: '#ff9800', fontWeight: 'bold' }}>{project.npsScore}/5</span>
                            ) : (
                              <span style={{ color: '#999' }}>N/A</span>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            ₹{Number(project.revenue || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Section 1: Designers Under — Hidden when opened from Design NPS column */}
              {!selectedODMData?.isNpsClick && (
                <>
                  <hr style={{ margin: '20px 0', borderColor: '#ddd' }} />
                  <div style={{ marginBottom: '30px' }}>
                    <Typography variant="h6" style={{ marginBottom: '15px', fontWeight: 'bold', color: '#1c375d' }}>
                      Designers Under {selectedODMData.userName}
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow style={{ backgroundColor: '#1c375d' }}>
                          <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>S. No</TableCell>
                          <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>Designer Name</TableCell>
                          <TableCell style={{ fontWeight: 'bold', color: '#fff' }}>Email</TableCell>
                          <TableCell style={{ fontWeight: 'bold', color: '#fff' }} align="center">Projects</TableCell>
                          <TableCell style={{ fontWeight: 'bold', color: '#fff' }} align="right">Total Revenue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedODMData?.designerDetails && selectedODMData.designerDetails.length > 0 ? (
                          selectedODMData.designerDetails.map((designer, index) => (
                            <TableRow key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f0f4f8' }}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell><strong>{designer.name}</strong></TableCell>
                              <TableCell>{designer.email || 'N/A'}</TableCell>
                              <TableCell align="center">{designer.projectsCount || 0}</TableCell>
                              <TableCell align="right">
                                ₹{Number(designer.totalRevenue || 0).toLocaleString('en-IN')}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan="5" style={{ textAlign: 'center' }}>No designers found</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProjectsModal} color="primary" variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* MONTHLY POINTS CHART MODAL */}
      <Dialog open={chartModalOpen} onClose={() => setChartModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ backgroundColor: '#1c375d', color: '#fff' }}>
          📈 Monthly Points Trend
        </DialogTitle>
        <DialogContent dividers style={{ minHeight: '500px', padding: '30px' }}>
          {monthlyChartData && (
            <Line
              data={monthlyChartData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: {
                    display: true,
                    position: 'top',
                    labels: {
                      color: '#1c375d',
                      font: { size: 14, weight: 'bold' },
                      padding: 20
                    }
                  },
                  tooltip: {
                    backgroundColor: '#1c375d',
                    padding: 12,
                    titleColor: '#fdb913',
                    bodyColor: '#fff',
                    borderColor: '#fdb913',
                    borderWidth: 2,
                    titleFont: { size: 13, weight: 'bold' },
                    bodyFont: { size: 12 }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      color: 'rgba(0,0,0,0.05)',
                      drawBorder: true,
                      borderColor: '#e0e0e0'
                    },
                    ticks: {
                      color: '#666',
                      font: { size: 12 },
                      callback: (value) => formatPoints(value)
                    },
                    title: {
                      display: true,
                      text: 'Total Points',
                      color: '#1c375d',
                      font: { size: 14, weight: 'bold' }
                    }
                  },
                  x: {
                    grid: {
                      display: false,
                      drawBorder: true,
                      borderColor: '#e0e0e0'
                    },
                    ticks: {
                      color: '#666',
                      font: { size: 12 }
                    },
                    title: {
                      display: true,
                      text: 'Month',
                      color: '#1c375d',
                      font: { size: 14, weight: 'bold' }
                    }
                  }
                }
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChartModalOpen(false)} color="primary" variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Redemption Modal */}
      {selectedUserForRedemption && (
        <RedemptionModal
          open={redemptionModalOpen}
          onClose={() => {
            setRedemptionModalOpen(false);
            setSelectedUserForRedemption(null);
          }}
          userPoints={selectedUserForRedemption.totalPoints}
          userAchievementPct={selectedUserForRedemption.achievedPercentage}
          userId={selectedUserForRedemption._id}
        />
      )}
      {/* ===== My Rewards: Eligibility Criteria Modal ===== */}
      <Dialog
        open={eligibilityCriteriaModalOpen}
        onClose={() => setEligibilityCriteriaModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 14, overflow: 'hidden' } }}
      >
        <DialogTitle style={{ background: 'linear-gradient(135deg, #1c375d 0%, #2e5a8a 100%)', color: '#fff', padding: '18px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>{selectedEligibilityRow?.isRedemptionEligible ? '✅' : '❌'}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Eligibility Criteria</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                Status: <strong style={{ color: selectedEligibilityRow?.isRedemptionEligible ? '#a5d6a7' : '#ef9a9a' }}>
                  {selectedEligibilityRow?.isRedemptionEligible ? 'Eligible for Redemption' : 'Not Eligible for Redemption'}
                </strong>
              </div>
            </div>
          </div>
        </DialogTitle>
        <DialogContent style={{ padding: '24px', background: '#f8fafc' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Criterion 1: Consecutive months */}
            {(() => {
              const consec = selectedEligibilityRow?.consecutiveEligibleMonths || 0;
              const passed = consec >= cfg.minConsecutiveMonths;
              return (
                <div style={{
                  background: '#fff', borderRadius: 10, padding: '14px 18px',
                  borderLeft: `4px solid ${passed ? '#4caf50' : '#f44336'}`,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#1c375d' }}>Consecutive Months ≥ {cfg.minAchievementPct}% Achievement</div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        Required: <strong>{cfg.minConsecutiveMonths} consecutive months</strong> &nbsp;|&nbsp; Current: <strong>{consec}</strong>
                      </div>
                    </div>
                    <span style={{
                      background: passed ? '#e8f5e9' : '#ffebee',
                      color: passed ? '#2e7d32' : '#c62828',
                      borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 700
                    }}>{passed ? '✅ Passed' : `❌ Need ${cfg.minConsecutiveMonths - consec} more`}</span>
                  </div>
                </div>
              );
            })()}

            {/* Criterion 2: Cumulative achievement */}
            {(() => {
              const ach = selectedEligibilityRow?.achievedPercentage || 0;
              const passed = ach >= cfg.minAchievementPct;
              return (
                <div style={{
                  background: '#fff', borderRadius: 10, padding: '14px 18px',
                  borderLeft: `4px solid ${passed ? '#4caf50' : '#f44336'}`,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#1c375d' }}>Year-End Cumulative Achievement</div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        Required: <strong>≥ {cfg.minAchievementPct}%</strong> &nbsp;|&nbsp; Current: <strong>{ach}%</strong>
                      </div>
                    </div>
                    <span style={{
                      background: passed ? '#e8f5e9' : '#ffebee',
                      color: passed ? '#2e7d32' : '#c62828',
                      borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 700
                    }}>{passed ? '✅ Passed' : `❌ ${ach}% (Need ≥${cfg.minAchievementPct}%)`}</span>
                  </div>
                </div>
              );
            })()}

            {/* Criterion 3: Employee status */}
            {(() => {
              const blocked = selectedEligibilityRow?.statusBlockReason;
              const passed = !blocked;
              return (
                <div style={{
                  background: '#fff', borderRadius: 10, padding: '14px 18px',
                  borderLeft: `4px solid ${passed ? '#4caf50' : '#f44336'}`,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#1c375d' }}>Active Employment Status</div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        Must not be on PIP / Refresher / Notice Period
                        {blocked && <span style={{ color: '#c62828', fontWeight: 600 }}> — {blocked}</span>}
                      </div>
                    </div>
                    <span style={{
                      background: passed ? '#e8f5e9' : '#ffebee',
                      color: passed ? '#2e7d32' : '#c62828',
                      borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 700
                    }}>{passed ? '✅ Passed' : '❌ Blocked'}</span>
                  </div>
                </div>
              );
            })()}

            {/* Criterion 4: Redemption window */}
            {(() => {
              const now = new Date();
              const isDecember = now.getMonth() === 11;
              return (
                <div style={{
                  background: '#fff', borderRadius: 10, padding: '14px 18px',
                  borderLeft: `4px solid ${isDecember ? '#4caf50' : '#ff9800'}`,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#1c375d' }}>Redemption Window</div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        Redemption is only open in <strong>December</strong> &nbsp;|&nbsp; Current month: <strong>{now.toLocaleString('default', { month: 'long' })}</strong>
                      </div>
                    </div>
                    <span style={{
                      background: isDecember ? '#e8f5e9' : '#fff3e0',
                      color: isDecember ? '#2e7d32' : '#e65100',
                      borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 700
                    }}>{isDecember ? '✅ Open' : '📅 Not Open Yet'}</span>
                  </div>
                </div>
              );
            })()}

            {/* Summary note */}
            <div style={{
              background: selectedEligibilityRow?.isRedemptionEligible ? '#e8f5e9' : '#fff8e1',
              border: `1px solid ${selectedEligibilityRow?.isRedemptionEligible ? '#a5d6a7' : '#ffe082'}`,
              borderRadius: 8, padding: '10px 14px', marginTop: 4
            }}>
              <Typography style={{ fontSize: 12, color: '#555', lineHeight: 1.6 }}>
                {selectedEligibilityRow?.isRedemptionEligible
                  ? '🎉 You meet all eligibility criteria! Redemption will be available in December.'
                  : '📌 Please meet all the above criteria to become eligible for redemption. Keep achieving your monthly targets consistently!'}
              </Typography>
            </div>
          </div>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px', background: '#f8fafc' }}>
          <Button onClick={() => setEligibilityCriteriaModalOpen(false)} variant="contained"
            style={{ background: '#1c375d', color: '#fff', borderRadius: 8, textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== My Rewards: Redemption Criteria Info Modal ===== */}
      <Dialog
        open={redemptionInfoModalOpen}
        onClose={() => setRedemptionInfoModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 14, overflow: 'hidden' } }}
      >
        <DialogTitle style={{ background: 'linear-gradient(135deg, #1c375d 0%, #2e5a8a 100%)', color: '#fff', padding: '18px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>💎</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Redemption Criteria</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>How your points are converted to rewards</div>
            </div>
          </div>
        </DialogTitle>
        <DialogContent style={{ padding: '24px', background: '#f8fafc' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Eligibility requirements */}
            <div style={{ background: '#fff', borderRadius: 10, padding: '16px', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1c375d', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>🎯</span> Eligibility Requirements
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  ['📅', 'Redemption window', 'December only (once per year)'],
                  ['📈', 'Consecutive months', `≥ ${cfg.minAchievementPct}% achievement for ${cfg.minConsecutiveMonths}+ consecutive months`],
                  ['🏆', 'Year-end achievement', `≥ ${cfg.minAchievementPct}% cumulative achievement`],
                  ['✅', 'Active status', 'Not on PIP / Refresher / Notice Period'],
                ].map(([icon, label, desc], i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', paddingBottom: 8, borderBottom: i < 3 ? '1px solid #f0f0f0' : 'none' }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 12, color: '#1c375d' }}>{label}</div>
                      <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Redemption tiers */}
            <div style={{ background: '#fff', borderRadius: 10, padding: '16px', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1c375d', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>💰</span> Redemption Tiers (% of Total Points)
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#1c375d', color: '#fdb913' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', borderRadius: '6px 0 0 0' }}>Achievement %</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center' }}>Points Redeemable</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', borderRadius: '0 6px 0 0' }}>Payout</th>
                  </tr>
                </thead>
                <tbody>
                  {cfg.redemptionTiers.length > 0 ? cfg.redemptionTiers.map((tier, i) => {
                    const pct = `${Math.round(tier.redeemPercentage * 100)}%`;
                    const minPct = tier.minPoints > 100 ? Math.floor(tier.minPoints / 1000) : tier.minPoints;
                    const maxPct = tier.maxPoints ? (tier.maxPoints > 100 ? Math.floor(tier.maxPoints / 1000) : tier.maxPoints) : null;
                    const range = maxPct !== null ? `${minPct} – ${maxPct}%` : `≥ ${minPct}%`;
                    const payout = tier.redeemPercentage === 0 ? 'Not eligible' : `${pct} of points`;
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1c375d' }}>{range}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', color: tier.redeemPercentage === 0 ? '#f44336' : '#2e7d32', fontWeight: 700 }}>{pct}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#555' }}>{payout}</td>
                      </tr>
                    );
                  }) : [
                    ['≥ 91%', '100%', 'Full points'],
                    ['81 – 90%', '90%', '90% of points'],
                    ['71 – 80%', '75%', '75% of points'],
                    ['65 – 70%', '50%', '50% of points'],
                    ['< 65%', '0%', 'Not eligible'],
                  ].map(([range, pct, payout], i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1c375d' }}>{range}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center', color: pct === '0%' ? '#f44336' : '#2e7d32', fontWeight: 700 }}>{pct}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: '#555' }}>{payout}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            
          </div>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px', background: '#f8fafc' }}>
          <Button onClick={() => setRedemptionInfoModalOpen(false)} variant="contained"
            style={{ background: '#1c375d', color: '#fff', borderRadius: 8, textTransform: 'none', fontWeight: 600 }}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default connect(null, { setLoading, setSnackInfo })(ERPSDashBoard);